import { Controller } from '@hotwired/stimulus'

// Каталог маленький (десятки товаров) — весь список подсказок отдаётся сразу
// со страницей (ComparesController#new), а поиск фильтрует его на клиенте.
// Без этого лимита при пустом запросе на экране была бы вся выдача разом.
const RESULTS_LIMIT = 8

// Совпадает с ComparesController::INDEXABLE_ITEMS.max: 2-3 товара получают
// путь в адресе (свой индексируемый лендинг — их реально ищут в Google),
// 4-10 — ?products= с noindex (личная подборка, свой лендинг ей не нужен).
const PATH_ITEMS_MAX = 3

// Тот же ключ, что и у compare_bar_controller.js (кнопка "Add to compare" на
// странице товара) — раз ракетку убрали из сравнения прямо в конструкторе,
// она не должна продолжать числиться "добавленной" на её собственной странице.
const STORAGE_KEY = 'padelmesh:compare-slugs'

/**
 * @file Конструктор сравнения ракеток: строка поиска добавляет ракетки в
 * слоты (2-10), кнопка «Compare» ведёт на страницу результата — состояние
 * целиком в её URL (см. ComparesController#show, PATH_ITEMS_MAX). Список для
 * поиска (все видимые товары) приходит один раз при открытии страницы, само
 * нажатие клавиши на сервер не ходит.
 *
 * Выпадашка подсказок переиспользует разметку/классы search-suggestions__*
 * из search_form_controller.js — тот же визуальный язык, но выбор здесь не
 * уводит со страницы, а заполняет слот.
 */
export default class ComparePickerController extends Controller {
  static targets = [
    'input',
    'results',
    'slots',
    'slotsTrack',
    'submit',
    'prevArrow',
    'nextArrow'
  ]

  static values = {
    products: Array,
    comparePath: String,
    compareQueryPath: String,
    productPath: String,
    maxSlots: { type: Number, default: 3 },
    minSlots: { type: Number, default: 2 },
    initial: Array,
    addLabel: String,
    removeLabel: String
  }

  #selected = []
  #entries = []

  // Не element.contains: внутри контроллера есть и другие пустые слоты — по
  // виду часть "пустого места" страницы, а по DOM всё ещё сам виджет. Закрываем
  // по клику вне строки поиска и самой выпадашки, а не вне виджета целиком.
  //
  // Клик по пустому слоту ("+") тоже исключаем: он сам открывает выпадашку
  // через focusSearch, а этот же клик всплывает сюда следом — без исключения
  // обработчик закрывал бы её в тот же момент, что и открытие.
  #handleOutsideClick = (event) => {
    if (this.inputTarget.contains(event.target)) return
    if (this.hasResultsTarget && this.resultsTarget.contains(event.target))
      return
    if (event.target.closest('[data-action*="focusSearch"]')) return

    this.#closeResults()
  }

  connect() {
    if (this.hasInitialValue && this.initialValue.length) {
      // slice, а не просто весь initialValue: конструктор может открыться с
      // возврата из результата сравнения крупнее текущего maxSlotsValue (см.
      // историю MAX_ITEMS) — без обрезки лишние слоты просто не поместились
      // бы в разметку слотов.
      this.#selected = this.initialValue
        .filter((item) => item?.slug)
        .slice(0, this.maxSlotsValue)
    }
    this.#renderSlots()
    document.addEventListener('click', this.#handleOutsideClick)
    if (this.hasSlotsTrackTarget) {
      this.slotsTrackTarget.addEventListener('scroll', this.#updateArrows, {
        passive: true
      })
    }
    window.addEventListener('resize', this.#handleResize)
  }

  disconnect() {
    document.removeEventListener('click', this.#handleOutsideClick)
    if (this.hasSlotsTrackTarget) {
      this.slotsTrackTarget.removeEventListener('scroll', this.#updateArrows)
    }
    window.removeEventListener('resize', this.#handleResize)
  }

  #handleResize = () => {
    this.#clampTrackWidth()
    this.#updateArrows()
  }

  // Слоты — не карточки-товары на странице результата (там несколько строк
  // синхронно скроллятся controller'ом compare-scroll-sync), а единственная
  // строка, поэтому прокрутка — тут же, без отдельного контроллера.
  scrollPrev() {
    if (!this.hasSlotsTrackTarget) return

    this.slotsTrackTarget.scrollBy({
      left: -this.#itemPitch(),
      behavior: 'smooth'
    })
  }

  scrollNext() {
    if (!this.hasSlotsTrackTarget) return

    this.slotsTrackTarget.scrollBy({
      left: this.#itemPitch(),
      behavior: 'smooth'
    })
  }

  // Шаг проматывания — ширина одного слота вместе с gap до соседнего (не
  // просто его собственная ширина: без учёта gap стрелка недокручивала бы до
  // ровного края). Меряем по факту через offsetLeft соседних слотов, а не
  // читаем gap из CSS — тогда работает при любом брейкпоинте без дублирования
  // самого значения gap здесь.
  #itemPitch() {
    if (!this.hasSlotsTarget) return this.slotsTrackTarget.clientWidth

    const slots = this.slotsTarget
    if (slots.children.length < 2) return this.slotsTrackTarget.clientWidth

    return slots.children[1].offsetLeft - slots.children[0].offsetLeft
  }

  // Фильтрация целиком локальная — ни дебаунса, ни похода на сервер не нужно.
  search() {
    this.#renderResults(this.#filter(this.inputTarget.value))
  }

  // Клик по пустому слоту ("+"): фокусируем строку поиска и сразу открываем
  // выпадашку — не заставляем сначала догадаться, что нужно печатать.
  focusSearch() {
    this.inputTarget.focus()
    this.#renderResults(this.#filter(this.inputTarget.value))
  }

  select(event) {
    const index = Number(event.currentTarget.dataset.index)
    const entry = this.#entries[index]
    if (!entry) return

    this.#addSlot(entry)
  }

  remove(event) {
    const slug = event.currentTarget.dataset.slug
    this.#selected = this.#selected.filter((item) => item.slug !== slug)
    this.#removeFromStoredList(slug)
    this.#renderSlots()
  }

  #removeFromStoredList(slug) {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      const list = raw ? JSON.parse(raw) : []
      if (!Array.isArray(list)) return

      const next = list.filter((storedSlug) => storedSlug !== slug)
      if (next.length !== list.length) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      }
    } catch {
      // Приватный режим браузера и подобное — персистентного списка просто
      // нет, синхронизировать с ним нечего.
    }
  }

  // Симметрично #removeFromStoredList: слот добавили прямо в конструкторе
  // (а не перешли на него с "Compare"/"Add to compare" другой страницы) —
  // без этого он не считался бы добавленным на собственной странице товара
  // и терялся бы из накопленного списка при следующем визите туда.
  #addToStoredList(slug) {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      const parsed = raw ? JSON.parse(raw) : []
      const list = Array.isArray(parsed)
        ? parsed.filter((storedSlug) => typeof storedSlug === 'string')
        : []

      // Не длиннее числа слотов: конструктор мог открыться с подмножеством
      // (?add=), пока в хранилище уже полный список — без границы бейдж на
      // страницах товаров обещал бы больше maxSlots, а сервер (см.
      // ComparesController#parse_slugs) молча отбрасывал бы как раз самые
      // свежие добавления.
      if (!list.includes(slug) && list.length < this.maxSlotsValue) {
        list.push(slug)
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
      }
    } catch {
      // Приватный режим браузера и подобное — персистентного списка просто
      // нет, писать в него некуда.
    }
  }

  submit(event) {
    if (this.#selected.length < this.minSlotsValue) event.preventDefault()
  }

  // Токены запроса должны встретиться в названии или бренде все разом (порядок
  // не важен) — так «head coello» находит «HEAD Coello Pro» вне зависимости
  // от того, в каком поле какое слово. Уже выбранные слоты из выдачи убираем:
  // выбрать одну ракетку дважды нельзя.
  #filter(rawQuery) {
    const excluded = new Set(this.#selected.map((item) => item.slug))
    const available = this.productsValue.filter(
      (item) => !excluded.has(item.slug)
    )

    const tokens = rawQuery.trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (tokens.length === 0) return available.slice(0, RESULTS_LIMIT)

    return available
      .filter((item) => {
        const haystack = `${item.name} ${item.brand || ''}`.toLowerCase()
        return tokens.every((token) => haystack.includes(token))
      })
      .slice(0, RESULTS_LIMIT)
  }

  #addSlot(entry) {
    if (this.#selected.some((item) => item.slug === entry.slug)) return
    if (this.#selected.length >= this.maxSlotsValue) return

    this.#selected.push(entry)
    this.#addToStoredList(entry.slug)
    this.inputTarget.value = ''
    this.#closeResults()
    this.#renderSlots()
    // Новый пустой слот ("+") мог оказаться за пределами видимой области —
    // доскролливаем до него, а не оставляем обрезанным с края ряда.
    this.#scrollToLastSlot()
  }

  #scrollToLastSlot() {
    if (!this.hasSlotsTarget) return

    const last = this.slotsTarget.lastElementChild
    if (last) {
      last.scrollIntoView({
        behavior: 'smooth',
        inline: 'end',
        block: 'nearest'
      })
    }
  }

  #renderResults(entries) {
    this.#entries = entries
    if (!this.hasResultsTarget) return

    const dropdown = this.resultsTarget
    dropdown.innerHTML = ''

    if (entries.length === 0) {
      this.#closeResults()
      return
    }

    entries.forEach((entry, index) =>
      dropdown.appendChild(this.#buildResultItem(entry, index))
    )
    dropdown.classList.remove('hidden')
  }

  #buildResultItem(entry, index) {
    const item = document.createElement('button')
    item.type = 'button'
    item.className = 'search-suggestions__item'
    // <button> не растягивается на всю ширину даже с display: flex (в отличие
    // от <a>, которым строит те же .search-suggestions__item общий поиск) —
    // без этого подсветка при наведении/клавиатуре обрывается на середине.
    item.style.width = '100%'
    item.dataset.index = String(index)
    item.dataset.action = 'click->compare-picker#select'

    if (entry.thumbnail_url) {
      const image = document.createElement('img')
      image.className = 'search-suggestions__image'
      image.src = entry.thumbnail_url
      image.alt = ''
      image.width = 40
      image.height = 40
      item.appendChild(image)
    }

    const body = document.createElement('span')
    body.className = 'search-suggestions__body'

    const name = document.createElement('span')
    name.className = 'search-suggestions__name'
    name.textContent = entry.name
    body.appendChild(name)

    if (entry.brand) {
      const subtitle = document.createElement('span')
      subtitle.className = 'search-suggestions__subtitle'
      subtitle.textContent = entry.brand
      body.appendChild(subtitle)
    }

    item.appendChild(body)
    return item
  }

  #closeResults() {
    this.#entries = []
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ''
      this.resultsTarget.classList.add('hidden')
    }
  }

  #renderSlots() {
    if (!this.hasSlotsTarget) return

    const slots = this.slotsTarget
    slots.innerHTML = ''

    this.#selected.forEach((item) =>
      slots.appendChild(this.#buildFilledSlot(item))
    )
    // Один пустой слот, а не до maxSlotsValue разом: со всеми "Optional"
    // сразу конструктор с 2 выбранными из 7 выглядел наполовину пустым.
    const hasEmptySlot = this.#selected.length < this.maxSlotsValue
    if (hasEmptySlot) {
      slots.appendChild(this.#buildEmptySlot())
    }

    this.#clampTrackWidth()
    this.#updateArrows()
    this.#updateSubmit()
  }

  // Столбцов ровно по числу слотов — строка одна, без переноса. Ширина
  // колонки всегда равна тому же ориентиру "как в сравнении на 4", что и на
  // странице результата (см. show.slim): с 1-2 слотами карточки не
  // растягиваются на всю ширину ряда, а остаются стандартного размера —
  // лишнее место просто пустует справа. Видимая ширина ряда при этом почти
  // никогда не делится на слоты без остатка — если слотов больше, чем
  // помещается целиком, подрезаем сам track до кратного числа (лишнее уходит
  // в горизонтальный скролл вместо переноса, и ни один слот не рисуется
  // наполовину — см. compare_scroll_sync_controller.js, тот же приём для
  // страницы результата).
  #clampTrackWidth() {
    if (!this.hasSlotsTrackTarget || !this.hasSlotsTarget) return

    const track = this.slotsTrackTarget
    const slots = this.slotsTarget
    const totalSlots = slots.children.length

    track.style.width = ''
    // %-формула — чистая функция totalSlots (в отличие от Ruby-рендеренной
    // версии на странице результата, здесь не нужно отдельно хранить
    // "исходное" значение), но считать по ней нужно до того, как ниже она
    // будет заменена на фиксированный px, иначе на повторных вызовах (resize)
    // мерили бы уже подрезанное состояние. Ставим её и при единственном
    // слоте: без explicit grid-template-columns голый .grid растягивает
    // одну-единственную implicit-колонку на всю ширину ряда — удаление
    // последней выбранной ракетки визуально раздувало бы оставшийся пустой
    // слот вместо того, чтобы сохранить тот же эталонный размер.
    //
    // --compare-ref-count/--compare-ref-gaps — те же переменные, что и в
    // .compare-grid на странице результата (см. _show.scss): 3 колонки на
    // мобильном (класс стоит на самом .grid, см. new.slim), 4 — от min-1024.
    // Меньший эталон на мобильном — то же решение, что и там: 3 видимых
    // слота, остальные уходят в горизонтальный скролл вместо сжатия карточек
    // до нечитаемой ширины.
    slots.style.gridTemplateColumns = `repeat(${totalSlots}, calc((100% - var(--compare-ref-gaps) * 1rem) / var(--compare-ref-count)))`
    if (totalSlots < 2) return

    const pitch = this.#itemPitch()
    const cardWidth = slots.children[0].getBoundingClientRect().width
    if (pitch <= 0) return
    const gap = pitch - cardWidth

    // Фиксируем px — иначе clientWidth track ниже сам изменит %-базу
    // calc(100%...), и слоты продолжили бы сжиматься по кругу вместе с
    // обрезаемым track.
    slots.style.gridTemplateColumns = `repeat(${totalSlots}, ${cardWidth}px)`

    if (slots.scrollWidth <= track.clientWidth + 1) return

    // +2px допуска — то же самое послабление, что и на странице результата
    // (см. compare_scroll_sync_controller.js): без него субпиксельное
    // округление где-то на волосок не дотягивало бы до следующего слота,
    // хотя по факту помещается.
    const wholeCount = Math.min(
      totalSlots,
      Math.max(1, Math.floor((track.clientWidth + gap + 2) / pitch))
    )

    if (wholeCount < totalSlots) {
      track.style.width = `${wholeCount * pitch - gap}px`
    }
  }

  // Стрелки — подсказка, что есть ещё слоты за кадром, а не постоянный
  // элемент управления: прячем ту или другую, как только доскроллили до
  // соответствующего края (или скроллить вообще некуда).
  #updateArrows = () => {
    if (!this.hasSlotsTrackTarget) return

    const track = this.slotsTrackTarget

    if (this.hasPrevArrowTarget) {
      this.prevArrowTarget.style.display =
        track.scrollLeft > 1 ? 'flex' : 'none'
    }
    if (this.hasNextArrowTarget) {
      const canScrollMore =
        track.scrollWidth - track.clientWidth - track.scrollLeft > 1
      this.nextArrowTarget.style.display = canScrollMore ? 'flex' : 'none'
    }
  }

  #buildFilledSlot(item) {
    const slot = document.createElement('div')
    slot.className = 'compare-slot relative flex flex-col'

    const href = this.#productHref(item.slug)

    const imageWrap = document.createElement('div')
    imageWrap.className =
      'relative aspect-square rounded-3xl overflow-hidden bg-bg-page border border-border'

    if (item.rating_avg !== null) {
      const rating = document.createElement('span')
      rating.className =
        'absolute top-5 left-5 z-10 flex items-center justify-center min-w-9 h-9 px-2 rounded-full border border-border bg-bg-page text-sb-16 text-text-blue shadow-sm'
      rating.textContent = item.rating_avg
      imageWrap.appendChild(rating)
    }

    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className =
      'absolute top-5 right-5 z-10 flex items-center justify-center w-9 h-9 rounded-full border border-border bg-bg-page text-icon-stroke-tertiary shadow-sm'
    remove.dataset.slug = item.slug
    remove.dataset.action = 'click->compare-picker#remove'
    remove.setAttribute('aria-label', this.removeLabelValue || 'Remove')
    remove.innerHTML =
      '<svg width="14" height="14" fill="none" data-icon="cross"><use href="#icon-cross"></use></svg>'
    imageWrap.appendChild(remove)

    if (item.thumbnail_url) {
      // Картинка — ссылкой на страницу товара (как в show.slim у карточек
      // сравнения): кнопка удаления рядом остаётся отдельным элементом, а не
      // вложена в эту же ссылку, иначе клик по крестику дублировал бы переход.
      const imageLink = document.createElement('a')
      imageLink.href = href
      imageLink.className = 'block w-full h-full'

      const image = document.createElement('img')
      image.className = 'w-full h-full object-contain p-4'
      image.src = item.thumbnail_url
      image.alt = ''
      imageLink.appendChild(image)
      imageWrap.appendChild(imageLink)
    }

    slot.appendChild(imageWrap)

    // Прямоугольник без скруглений, отдельным контуром — не продолжение
    // скруглённой карточки картинки, а самостоятельная плашка с названием.
    const body = document.createElement('div')
    body.className = 'p-3 mt-3 border border-border bg-bg-page'

    // min-h-11 = 2 строки text-sb-16 (line-height 1.375rem): бронь высоты
    // ряда в _show.scss (.has-initial-selection) считает плашку двухстрочной —
    // без min-height однострочное название делало бы слот на 22px ниже брони,
    // и контент под гридом подскакивал бы вверх при монтаже контроллера.
    const name = document.createElement('a')
    name.href = href
    name.className =
      'block min-h-11 text-sb-16 line-clamp-2 hover:text-text-blue transition-colors'
    name.textContent = item.name
    body.appendChild(name)

    slot.appendChild(body)
    return slot
  }

  #buildEmptySlot() {
    const slot = document.createElement('button')
    slot.type = 'button'
    slot.dataset.action = 'click->compare-picker#focusSearch'
    slot.className =
      'flex flex-col items-center justify-center gap-2 aspect-square rounded-3xl border border-dashed border-border text-text-secondary text-r-14 hover:border-text-blue hover:text-text-blue transition-colors'

    const icon = document.createElement('span')
    icon.className =
      'flex items-center justify-center w-9 h-9 rounded-full bg-bg-gray-subtle text-text-secondary text-2xl leading-none'
    icon.textContent = '+'
    slot.appendChild(icon)

    const label = document.createElement('span')
    label.textContent = this.addLabelValue
    slot.appendChild(label)

    return slot
  }

  #updateSubmit() {
    if (!this.hasSubmitTarget) return

    const count = this.#selected.length
    const ready = count >= this.minSlotsValue

    this.submitTarget.href = ready ? this.#compareUrl() : '#'
    this.submitTarget.classList.toggle('opacity-50', !ready)
    this.submitTarget.classList.toggle('pointer-events-none', !ready)
    this.submitTarget.setAttribute('aria-disabled', String(!ready))
  }

  // 2-3 товара — путь (см. PATH_ITEMS_MAX), 4-10 — ?products=.
  #compareUrl() {
    const slugs = this.#selected.map((item) => item.slug)

    if (slugs.length <= PATH_ITEMS_MAX) {
      return this.comparePathValue.replace('__SLUGS__', slugs.join('/'))
    }

    const url = new URL(this.compareQueryPathValue, window.location.origin)
    url.searchParams.set('products', slugs.join(','))
    return `${url.pathname}${url.search}`
  }

  #productHref(slug) {
    if (!this.hasProductPathValue) return '#'

    return this.productPathValue.replace('__SLUG__', encodeURIComponent(slug))
  }
}
